import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, Stack, Typography, alpha } from '@mui/material';
import { useForm } from 'react-hook-form';
import { EditDialog } from '../../components/EditDialog/EditDialog';
import { LoadFactor, PartialFactor } from 'types/domainTypes';

interface LoadFactorsDialogProps {
  data: LoadFactor;
  onClose: () => void;
}

function ValueText({ label, value }: { label: string; value?: string }) {
  const theme = useTheme();

  return (
    <Stack direction="row" spacing={1}>
      <Typography variant="body2" color={theme.palette.common.black}>
        <span style={{ color: alpha(theme.palette.common.black, 0.6) }}>
          {label}
          :&nbsp;
        </span>
        {value}
      </Typography>
    </Stack>
  );
}

function PartialFactorSection({ partialFactor, title }: { partialFactor: PartialFactor; title: string }) {
  const { i18n, t } = useTranslation();

  return (
    <Stack spacing={2}>
      <Stack direction={'row'} alignItems="center" justifyContent="space-between">
        <strong>{title}</strong>
      </Stack>

      <ValueText
        label={t('project-details-page.load-factors-dialog.label-permanent-load-favourable')}
        value={partialFactor.permanentLoadFavourable.toLocaleString(i18n.language)}
      />
      <ValueText
        label={t('project-details-page.load-factors-dialog.label-permanent-load-unfavourable')}
        value={partialFactor.permanentLoadUnfavourable.toLocaleString(i18n.language)}
      />
      <ValueText
        label={t('project-details-page.load-factors-dialog.label-variable-action')}
        value={partialFactor.variableAction.toLocaleString(i18n.language)}
      />
      <ValueText
        label={t('project-details-page.load-factors-dialog.label-temperature')}
        value={partialFactor.temperature.toLocaleString(i18n.language)}
      />
      <ValueText
        label={t('project-details-page.load-factors-dialog.label-creep-effect-permanent')}
        value={partialFactor.creepEffectPermanent.toLocaleString(i18n.language)}
      />
      <ValueText
        label={t('project-details-page.load-factors-dialog.label-creep-effect-snow')}
        value={partialFactor.creepEffectSnow.toLocaleString(i18n.language)}
      />
    </Stack>
  );
}

export function LoadFactorsDialog({ data, onClose }: LoadFactorsDialogProps): ReactElement {
  const { t } = useTranslation();

  const { handleSubmit } = useForm<LoadFactor>();

  return (
    <EditDialog
      onClose={onClose}
      handleFormSubmit={handleSubmit(onClose)}
      isDirty={false}
      title={t('project-details-page.load-factors-dialog.view-load-factors')}
      saveActionText={t('project-details-page.load-factors-dialog.action-dismiss')}
    >
      <Stack spacing={4}>
        <PartialFactorSection partialFactor={data.uls} title={t('project-details-page.load-factors-dialog.uls')} />
        <PartialFactorSection partialFactor={data.sls} title={t('project-details-page.load-factors-dialog.sls')} />
      </Stack>
    </EditDialog>
  );
}
